import { useWallet } from '@solana/wallet-adapter-react';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { useState, useEffect, useMemo } from 'react';

interface WalletReadyResult {
  /** True once wallet detection has completed (or timed out) */
  isReady: boolean;
  /** True while actively detecting wallets */
  isDetecting: boolean;
  /** Number of installed wallets detected */
  installedCount: number;
}

/**
 * Hook that tracks wallet detection readiness.
 * Standard wallets (Phantom, Backpack, Jupiter) register asynchronously.
 * This hook waits for either:
 * 1. At least one "Installed" wallet to be detected, or
 * 2. A timeout to elapse (default 1500ms)
 */
export function useWalletReady(timeoutMs = 1500): WalletReadyResult {
  const { wallets } = useWallet();
  const [isReady, setIsReady] = useState(false);

  const installedCount = useMemo(
    () => wallets.filter((w) => w.readyState === WalletReadyState.Installed).length,
    [wallets],
  );

  useEffect(() => {
    // Ready immediately if we have installed wallets
    if (installedCount > 0) {
      setIsReady(true);
      return;
    }
    // Otherwise wait for detection or timeout
    const timeout = setTimeout(() => setIsReady(true), timeoutMs);
    return () => clearTimeout(timeout);
  }, [installedCount, timeoutMs]);

  // Also respond reactively when wallets are detected
  useEffect(() => {
    if (installedCount > 0 && !isReady) {
      setIsReady(true);
    }
  }, [installedCount, isReady]);

  return { isReady, isDetecting: !isReady, installedCount };
}
